import {
    type QueryClient,
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { counterpartyContract } from "@common/contracts/counterparty.contract";
import { counterpartyTinErrorSchema } from "@common/schemas/counterpartyTin.schema";
import {
    CounterpartyTinDenialReason,
    CounterpartyTinGrantAction,
} from "@common/types/counterpartyTin";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { SUPPRESS_ERROR_TOAST_HEADER } from "@lib/backendClient";
import { invalidateWorkspaceHistoryQueries } from "./auditLog.api";
import { counterpartiesQueryKeys } from "./counterparty.api";

const counterpartyContractClient = getBackendAPIClient(counterpartyContract);

export interface CounterpartyTinIds {
    workspaceId: string;
    counterpartyId: string;
}

export type MintTinGrantResult =
    | { status: "granted"; grant: string }
    | { status: "stepUpRequired" }
    | { status: "failed"; message: string | null };

export const counterpartyTinQueryKey = ({
    workspaceId,
    counterpartyId,
}: CounterpartyTinIds) =>
    ["counterparty-tin", workspaceId, counterpartyId] as const;

export function useCounterpartyTinQuery({
    ids,
    enabled,
}: {
    ids: CounterpartyTinIds;
    enabled: boolean;
}) {
    return useQuery({
        queryKey: counterpartyTinQueryKey(ids),
        queryFn: async () =>
            (
                await counterpartyContractClient.getCounterpartyTin({
                    params: ids,
                })
            ).body,
        enabled,
    });
}

/**
 * Mints the single-use, 60-second grant one proxied write or reveal carries.
 * Failures come back as a result instead of a toast, so the caller can offer
 * "Sign in again" for a stale session.
 */
export async function mintTinGrant({
    ids,
    action,
}: {
    ids: CounterpartyTinIds;
    action: CounterpartyTinGrantAction;
}): Promise<MintTinGrantResult> {
    return await requestGrant(() =>
        counterpartyContractClient.createCounterpartyTinGrant({
            params: ids,
            body: { action },
            extraHeaders: { [SUPPRESS_ERROR_TOAST_HEADER]: "true" },
        }),
    );
}

/** Mints the single-use, 60-second grant one Track1099 export with tax IDs carries. */
export async function mintTinExportGrant({
    workspaceId,
    taxYear,
    entityId,
}: {
    workspaceId: string;
    taxYear: number;
    entityId: number;
}): Promise<MintTinGrantResult> {
    return await requestGrant(() =>
        counterpartyContractClient.createCounterpartyTinExportGrant({
            params: { workspaceId },
            body: { taxYear, entityId },
            extraHeaders: { [SUPPRESS_ERROR_TOAST_HEADER]: "true" },
        }),
    );
}

async function requestGrant(
    send: () => Promise<{ body: { grant: string } }>,
): Promise<MintTinGrantResult> {
    try {
        const response = await send();
        return { status: "granted", grant: response.body.grant };
    } catch (error) {
        const tinError = isAxiosError(error) ? parseTinError(error) : null;
        if (!tinError) {
            return { status: "failed", message: null };
        }
        if (tinError.code === CounterpartyTinDenialReason.STEP_UP_REQUIRED) {
            return { status: "stepUpRequired" };
        }
        return { status: "failed", message: tinError.message };
    }
}

function parseTinError(error: Error) {
    const parsedError = counterpartyTinErrorSchema.safeParse(
        isAxiosError(error) ? error.response?.data : null,
    );
    return parsedError.success ? parsedError.data : null;
}

export function invalidateCounterpartyTin(
    queryClient: QueryClient,
    ids: CounterpartyTinIds,
) {
    return Promise.all([
        queryClient.invalidateQueries({
            queryKey: counterpartyTinQueryKey(ids),
        }),
        queryClient.invalidateQueries({
            queryKey: counterpartiesQueryKeys.byId(
                ids.workspaceId,
                ids.counterpartyId,
            ),
        }),
        invalidateWorkspaceHistoryQueries(queryClient, ids.workspaceId),
    ]);
}

export function useClearTinMutation(ids: CounterpartyTinIds) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            await counterpartyContractClient.clearCounterpartyTin({
                params: ids,
            });
        },
        onSuccess: () => invalidateCounterpartyTin(queryClient, ids),
    });
}

/** The backend's message for a refused TIN request, such as a tax ID that changed under the reviewer. */
export function getTinErrorMessage(error: Error): string | null {
    return parseTinError(error)?.message ?? null;
}

export function useReviewTinMutation(ids: CounterpartyTinIds) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (tinRevisionId: string) => {
            await counterpartyContractClient.reviewCounterpartyTin({
                params: ids,
                body: { tinRevisionId },
                extraHeaders: { [SUPPRESS_ERROR_TOAST_HEADER]: "true" },
            });
        },
        onSettled: () => invalidateCounterpartyTin(queryClient, ids),
    });
}

export function useRevokeTinReviewMutation(ids: CounterpartyTinIds) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            await counterpartyContractClient.revokeCounterpartyTinReview({
                params: ids,
                extraHeaders: { [SUPPRESS_ERROR_TOAST_HEADER]: "true" },
            });
        },
        onSettled: () => invalidateCounterpartyTin(queryClient, ids),
    });
}
